import { useCallback, useEffect, useState } from 'react'

import type { TBranding } from '../context/BrandingContext'
import useLocalStorage from './useLocalStorage'
import useBranding from './useBranding'
import usePublicApi from './usePublicApi'

type TWhiteLabelBranding = Omit<TBranding, 'isTgt'>

type TBrandingResponse = {
  branding: TWhiteLabelBranding
}

type TPublicApiState = {
  error: unknown
  res: {
    data: Partial<TBrandingResponse>
  }
  success: boolean | null
}

type TPublicApiActions = {
  sendRequest: (config: { url: string }) => void
}

type TUseLocalStorage = [
  (value?: string | null) => string | null,
  (value?: string | null) => void,
]

const tgtHostNames = ['localhost', '192.168.1.205', 'files.topgear-tuning.com']

const ENABLE_CACHE = false

const useWhiteLabel = () => {
  const [ready, setReady] = useState<boolean | null>(null)
  const [, setBranding] = useBranding()
  const [{ res, error, success }, { sendRequest }] = usePublicApi() as [
    TPublicApiState,
    TPublicApiActions,
  ]
  const [getLocalBranding, setLocalBranding] = useLocalStorage(
    'branding',
  ) as TUseLocalStorage

  const addCss = useCallback((color: string) => {
    const link = document.createElement('link')
    link.href = `/colors/${color}.css`
    link.type = 'text/css'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
  }, [])

  const removeFavicon = useCallback(() => {
    const links = document.querySelectorAll('link[rel*="icon"]')
    links.forEach((link) => link.remove())

    const manifest = document.querySelector('link[rel*="manifest"]')

    if (manifest) {
      manifest.remove()
    }

    const mstiles = document.querySelectorAll('meta[name*="msapplication"]')
    mstiles.forEach((tile) => tile.remove())
  }, [])

  const updateTitle = useCallback((title: string) => {
    document.title = title
  }, [])

  useEffect(() => {
    if (tgtHostNames.includes(window.location.hostname)) {
      setBranding({
        isTgt: true,
        primaryColorPalette: 'blue',
        secondaryColorPalette: 'gray',
      })

      addCss('blue')
      addCss('gray')
      updateTitle('Topgear Tuning')
      setReady(true)
      return
    }

    if (ENABLE_CACHE) {
      const localBrandingValue = getLocalBranding()

      if (localBrandingValue) {
        const localBranding = JSON.parse(localBrandingValue) as TBranding

        setBranding(localBranding)
        addCss(localBranding.primaryColorPalette || 'blue')
        addCss(localBranding.secondaryColorPalette || 'gray')
        setReady(true)
        return
      }
    }

    sendRequest({
      url: `/branding/${window.location.hostname}`,
    })
  }, [setBranding, getLocalBranding, sendRequest, addCss, updateTitle])

  useEffect(() => {
    const branding = res.data.branding

    if (!success || !branding) {
      return
    }

    const brandingValue: TBranding = { isTgt: false, ...branding }

    setBranding(brandingValue)

    if (ENABLE_CACHE) {
      setLocalBranding(JSON.stringify(brandingValue))
    }

    addCss(branding.primaryColorPalette || 'blue')
    addCss(branding.secondaryColorPalette || 'gray')
    removeFavicon()
    updateTitle(branding.masterDealerName || 'Topgear Tuning')
    setReady(true)
  }, [
    res,
    setLocalBranding,
    success,
    setBranding,
    addCss,
    removeFavicon,
    updateTitle,
  ])

  return [ready, error] as const
}

export default useWhiteLabel
